/**
 * Exercise Error Boundary
 * Lightweight inline error boundary for individual exercises.
 * Shows a small error card instead of crashing the entire lesson.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { createLogger } from '../../utils/logger';

const log = createLogger('ExerciseErrorBoundary');

interface Props {
  exerciseId: string;
  exerciseType: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ExerciseErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    log.error('Exercise render failed', error, {
      exerciseId: this.props.exerciseId,
      exerciseType: this.props.exerciseType,
      componentStack: errorInfo.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <View style={styles.container}>
          <Text style={styles.icon}>⚠️</Text>
          <Text style={styles.text}>
            This exercise couldn't be displayed.
          </Text>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#FCD34D',
    alignItems: 'center',
    gap: 4,
  },
  icon: {
    fontSize: 24,
  },
  text: {
    fontSize: 13,
    color: '#92400E',
    textAlign: 'center',
  },
});
